import { useAppDispatch } from '@/store/hooks';
import { getNestedString, useConfig } from '@/utils/useConfig';
import { closeDialog, openDialog } from '@fuse/core/FuseDialog/fuseDialogSlice';
import FuseLoading from '@fuse/core/FuseLoading';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import React, { useEffect } from 'react';
import MarkdownRenderer from '../MarkdownRenderer';

/**
 * TermsLink Component
 * 
 * A clickable link component that opens a modal dialog displaying the terms of service.
 * The terms content is loaded from the application configuration and rendered as markdown.
 * The modal includes a close button and uses the Fuse dialog system.
 * 
 * @returns JSX element containing a clickable terms of service link
 */
function TermsLink() {

	const dispatch = useAppDispatch();

	const { config, loading, error, refresh } = useConfig('terms_config');

	useEffect(() => {
		refresh();
	}, [refresh]);

	/**
	 * Handles opening the terms of service modal dialog.
	 * Creates a dialog with the terms title and content from configuration.
	 */
	const handleOpenTermsDialog = () => {
		dispatch(
			openDialog({
				children: (
					<React.Fragment>
						{/* Dialog Header with Title and Close Button */}
						<DialogTitle
							id="alert-dialog-title"
							className="flex items-center justify-between"
						>
							Terms and Conditions
							<IconButton
								onClick={() => dispatch(closeDialog())}
								color="error"
							>
								<FuseSvgIcon
									size={12}
									className="arrow-icon"
								>
									heroicons-outline:x-mark
								</FuseSvgIcon>
							</IconButton>
						</DialogTitle>

						<DialogContent>
							{
								loading ? <FuseLoading /> : error ? <p>{error}</p> : <MarkdownRenderer markdown={getNestedString(config, 'content')} />
							}
						</DialogContent>

					</React.Fragment>
				),
				options: {
					maxWidth: false,
					paperClassName: 'max-w-4xl w-full'
				}
			})
		);
	};

	return (
		<button
			type="button"
			className="underline text-primary"
			onClick={handleOpenTermsDialog}
		>
			Terms of Service
		</button>
	);
}

export default TermsLink;
